import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Unlink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Modal } from "./Dialogs";

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Same rules as backend/assets.py: only real files, ignoring tracking/player hosts.
const LINK_RELS = ["stylesheet", "icon", "apple-touch-icon", "mask-icon", "preload", "shortcut"];
const SKIP_HOSTS = ["googletagmanager.com", "google-analytics.com", "googleadservices.com", "googleads.", "doubleclick.net", "googlesyndication.com", "connect.facebook.net", "facebook.com", "fbcdn.net", "hotjar.com", "clarity.ms", "tiktok.com", "sc-static.net", "snapchat.com", "criteo", "taboola", "outbrain", "rdstation", "mixpanel", "segment.io", "segment.com", "intercom", "crisp.chat", "tawk.to", "zendesk", "hubspot", "clevertap", "smartlook", "vwo.com", "recaptcha", "pandavideo.com.br", "vturb", "converteai", "youtube.com", "youtu.be", "ytimg.com", "vimeo.com", "vimeocdn.com", "wistia", "jwplayer", "cdn-reach.hostinger.com", "bing.com", "clickmagick", "utmify", "hotmart.com", "ticto", "kiwify", "monetizze"];
const TAG_RE = /<(link|script|img|source|video|audio|embed)\b([^>]*)>/gi;
const ATTR_RE = /\b(href|src|srcset|poster|data-src|data-srcset)\s*=\s*["']([^"']+)["']/gi;
const CSS_BLOCK_RE = /<style\b[^>]*>([\s\S]*?)<\/style>|\bstyle\s*=\s*"([^"]+)"/gi;
const CSS_URL_RE = /url\(\s*["']?([^)"']+)/gi;

export const countExternal = (html) => {
  const base = /<base\b[^>]*href\s*=\s*["']([^"']+)["']/i.exec(html)?.[1];
  const hosts = new Set();
  const seen = new Set();
  const add = (raw) => {
    raw = (raw || "").trim();
    if (!raw || raw.includes("/api/files/") || seen.has(raw)) return;
    let abs = raw;
    if (!/^https?:\/\//i.test(raw)) { if (!base) return; try { abs = new URL(raw, base).href; } catch { return; } }
    if (SKIP_HOSTS.some((s) => abs.toLowerCase().includes(s))) return;
    seen.add(raw);
    try { hosts.add(new URL(abs).hostname); } catch { /* ignore */ }
  };
  let m;
  TAG_RE.lastIndex = 0;
  while ((m = TAG_RE.exec(html))) {
    const tag = m[1].toLowerCase(), attrs = m[2];
    const rel = (/\brel\s*=\s*["']([^"']+)["']/i.exec(attrs)?.[1] || "").toLowerCase();
    ATTR_RE.lastIndex = 0;
    let a;
    while ((a = ATTR_RE.exec(attrs))) {
      const attr = a[1].toLowerCase();
      if (attr === "href" && (tag !== "link" || !LINK_RELS.some((r) => rel.includes(r)))) continue;
      if (attr.includes("srcset")) a[2].split(",").forEach((part) => add(part.trim().split(" ")[0]));
      else add(a[2]);
    }
  }
  CSS_BLOCK_RE.lastIndex = 0;
  let c;
  while ((c = CSS_BLOCK_RE.exec(html))) {
    const css = c[1] || c[2] || "";
    CSS_URL_RE.lastIndex = 0;
    let u;
    while ((u = CSS_URL_RE.exec(css))) add(u[1]);
  }
  return { count: seen.size, hosts: [...hosts].slice(0, 4), independent: seen.size === 0 };
};

export const MirrorDialog = ({ project, ed, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      // big WordPress pages exceed one round's budget, so keep going while it progresses
      let last = null, rounds = 0, files = 0, bytes = 0;
      do {
        const { data } = await api.post(`/projects/${project.id}/mirror`, { html: rounds ? undefined : ed.serialize(), apply: true });
        files += data.mirrored;
        bytes += data.added_bytes;
        if (data.mirrored) ed.replaceHtml(data.html, { record: rounds === 0 });
        const progressed = last === null || data.remaining < last;
        last = data.remaining;
        rounds++;
        if (!data.remaining || !progressed || rounds >= 6) { setRes({ ...data, mirrored: files, added_bytes: bytes }); break; }
      } while (true);
      if (files) toast.success(`${files} arquivo(s) espelhado(s) · ${kb(bytes)}`);
      else if (!last) toast.info("Nada a espelhar: essa página já não busca arquivos de fora.");
      else toast.error("Nenhum arquivo pôde ser baixado do site de origem.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Espelhar arquivos da origem" onClose={onClose} testid="mirror-dialog">
      <p className="text-sm text-slate-600">Baixa o <strong>CSS, as fontes, os scripts e as imagens</strong> que a página ainda carrega do site original e passa a servir tudo do Lowpages. Assim o layout não quebra se a origem sair do ar ou mudar.</p>
      <ul className="mt-3 text-xs text-slate-500 space-y-1">
        <li>• Segue os <code>@import</code> e os <code>url()</code> dentro dos CSS para pegar as fontes</li>
        <li>• Até 400 arquivos, 8 MB cada, 40 MB por rodada — se faltar, rode de novo e continuamos</li>
        <li>• Pixels e players de vídeo (Facebook, GA, Panda, YouTube) continuam na origem de propósito</li>
        <li>• Quando tudo é espelhado, removemos a marca <code>&lt;base&gt;</code> da clonagem</li>
      </ul>

      {!res && <button data-testid="mirror-run" disabled={busy} onClick={run} className="btn btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? <><Loader2 size={15} className="animate-spin" /> Baixando arquivos… pode levar 1 min</> : <><Unlink size={15} /> Espelhar agora</>}</button>}

      {res && (
        <div data-testid="mirror-result" className="mt-5">
          <div className="rounded-2xl border border-slate-200 p-4 flex items-center justify-around text-center">
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">arquivos</div><div data-testid="mirror-count" className="font-display font-bold text-lg text-emerald-600">{res.mirrored}</div></div>
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">referências</div><div data-testid="mirror-found" className="font-display font-bold text-lg">{res.found}</div></div>
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">baixado</div><div data-testid="mirror-bytes" className="font-display font-bold text-lg">{kb(res.added_bytes)}</div></div>
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">origem</div><div data-testid="mirror-independent" className={`font-display font-bold text-lg ${res.independent ? "text-emerald-600" : "text-amber-600"}`}>{res.independent ? "livre" : "parcial"}</div></div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {res.independent ? "A página não depende mais do site de origem." : `${res.remaining ?? res.failed} arquivo(s) continuam sendo carregados do site original${res.partial ? " (limite de tamanho/tempo atingido — rode de novo para continuar)" : ""}.`}
          </p>
          <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
            {res.assets.map((a, n) => (
              <div key={n} data-testid={`mirror-item-${n}`} className="flex items-center justify-between gap-2 text-[11px] font-mono rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5">
                <span className="truncate text-slate-500">{a.kind} · {a.ref}</span>
                <span className={a.status === "espelhado" ? "text-emerald-600 shrink-0" : "text-amber-600 shrink-0"}>{a.status === "espelhado" ? kb(a.size) : "na origem"}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="mirror-again" onClick={() => setRes(null)} className="btn btn-ghost flex-1 justify-center">Rodar de novo</button>
            <button data-testid="mirror-close" onClick={onClose} className="btn btn-primary flex-1 justify-center">Pronto</button>
          </div>
        </div>
      )}
    </Modal>
  );
};
